import { call, put, take, takeEvery } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { api } from 'services';
import { notificationContainer } from 'utils/notificationContainer';
import {
	getShopifyAppsRequest,
	getShopifyAppsSuccess,
	getShopifyStoresRequest,
	getShopifyStoresSuccess,
	addShopifyAppRequest,
	addShopifyAppSuccess,
	installShopifyStoreRequest,
	installShopifyStoreSuccess,
} from './reducer';
import {
	TAddShopifyAppRequest,
	TInstallShopifyStoreRequest,
	TInstallShopifyStoreResponse,
	TShopifyAppsRequest,
	TShopifyAppsResponsePayload,
	TShopifyStoresRequest,
	TShopifyStoresResponsePayload,
} from './types';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

function* getShopifyAppsRequestWorker(action: PayloadAction<TShopifyAppsRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TShopifyAppsResponsePayload = yield call(
			api.shopifyApps.getShopifyApps,
			payload,
		);
		yield put(getShopifyAppsSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		}
		yield put(getShopifyAppsSuccess(null));
	}
}

function* getShopifyStoresRequestWorker(action: PayloadAction<TShopifyStoresRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TShopifyStoresResponsePayload = yield call(
			api.shopifyApps.getShopifyStores,
			payload,
		);
		yield put(getShopifyStoresSuccess(response));
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		}
		yield put(getShopifyStoresSuccess(null));
	}
}

function* addShopifyAppRequestWorker(action: PayloadAction<TAddShopifyAppRequest>) {
	const { onSuccess, ...payload } = action.payload;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		yield call(api.shopifyApps.addShopifyApp, payload);
		yield put(addShopifyAppSuccess());
		notificationContainer('Shopify app has been added', 'success');
		onSuccess?.();
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		}
		yield put(addShopifyAppSuccess());
	}
}

function* installShopifyStoreRequestWorker(action: PayloadAction<TInstallShopifyStoreRequest>) {
	const { onSuccess, ...payload } = action.payload;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TInstallShopifyStoreResponse = yield call(
			api.shopifyApps.installShopifyStore,
			payload,
		);
		yield put(installShopifyStoreSuccess());
		onSuccess?.(response);
	} catch (error) {
		if (axios.isAxiosError(error)) {
			notificationContainer(
				error?.response?.data?.message ?? error.message ?? 'Request error',
				'error',
			);
		}
		yield put(installShopifyStoreSuccess());
	}
}

export function* shopifyAppsSaga() {
	yield takeEvery(getShopifyAppsRequest.type, getShopifyAppsRequestWorker);
	yield takeEvery(getShopifyStoresRequest.type, getShopifyStoresRequestWorker);
	yield takeEvery(addShopifyAppRequest.type, addShopifyAppRequestWorker);
	yield takeEvery(installShopifyStoreRequest.type, installShopifyStoreRequestWorker);
}
